import { useCallback, useRef } from "react";

interface UseLongPressOptions {
  delay?: number;
  onLongPress: () => void;
  onClick?: () => void;
}

interface UseLongPressHandlers {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchEnd: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onMouseDown: (event: React.MouseEvent) => void;
  onMouseUp: (event: React.MouseEvent) => void;
  onMouseLeave: (event: React.MouseEvent) => void;
  onContextMenu: (event: React.MouseEvent) => void;
}

const DEFAULT_LONG_PRESS_DELAY_MS = 500;

export function useLongPress(options: UseLongPressOptions): UseLongPressHandlers {
  const { delay = DEFAULT_LONG_PRESS_DELAY_MS, onLongPress, onClick } = options;

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressRef = useRef(false);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      // eslint-disable-next-line prefer-destructuring
      const touch = event.touches[0];
      startPositionRef.current = { x: touch.clientX, y: touch.clientY };
      isLongPressRef.current = false;

      timerRef.current = setTimeout(() => {
        isLongPressRef.current = true;
        onLongPress();
      }, delay);
    },
    [delay, onLongPress]
  );

  const handleTouchEnd = useCallback(
    (_event: React.TouchEvent) => {
      clearTimer();

      if (!isLongPressRef.current && onClick) {
        onClick();
      }

      isLongPressRef.current = false;
      startPositionRef.current = null;
    },
    [clearTimer, onClick]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!startPositionRef.current) {
        return;
      }

      // eslint-disable-next-line prefer-destructuring
      const touch = event.touches[0];
      const moveThreshold = 10;

      const deltaX = Math.abs(touch.clientX - startPositionRef.current.x);
      const deltaY = Math.abs(touch.clientY - startPositionRef.current.y);

      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        clearTimer();
        startPositionRef.current = null;
      }
    },
    [clearTimer]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.button !== 0) {
        return;
      }

      startPositionRef.current = { x: event.clientX, y: event.clientY };
      isLongPressRef.current = false;

      timerRef.current = setTimeout(() => {
        isLongPressRef.current = true;
        onLongPress();
      }, delay);
    },
    [delay, onLongPress]
  );

  const handleMouseUp = useCallback(
    (_event: React.MouseEvent) => {
      clearTimer();

      if (!isLongPressRef.current && onClick) {
        onClick();
      }

      isLongPressRef.current = false;
      startPositionRef.current = null;
    },
    [clearTimer, onClick]
  );

  const handleMouseLeave = useCallback(
    (_event: React.MouseEvent) => {
      clearTimer();
      isLongPressRef.current = false;
      startPositionRef.current = null;
    },
    [clearTimer]
  );

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    if (isLongPressRef.current) {
      event.preventDefault();
    }
  }, []);

  return {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    onTouchMove: handleTouchMove,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseLeave,
    onContextMenu: handleContextMenu,
  };
}
